import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
  Typography,
} from "@mui/material";
import { isCombinationDisabled } from "../../utils/accessory";

const AccessoryCustomizationForm = ({
  customizationOptions,
  customizationValues,
  setCustomizationValues,
  validationErrors = {},
  onFieldUpdate,
  disabledCombinations = [],
}) => {
  const handleChange = (key, value) => {
    setCustomizationValues((prev) => {
      const updated = { ...prev, [key]: value };
      return updated;
    });

    if (onFieldUpdate) {
      onFieldUpdate(key, value);
    }
  };

  const handleCheckboxChange = (key, option) => {
    const current = customizationValues[key] || [];
    const updated = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    handleChange(key, updated);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
      }}>
      {customizationOptions.map((field, index) => {
        const key = field.key;
        const value = customizationValues[key] ?? "";
        const showError = !!validationErrors[key];

        const commonProps = {
          error: showError,
          helperText: showError ? validationErrors[key] : "",
        };

        switch (field.type) {
          case "select":
            return (
              <TextField
                key={index}
                select
                label={field.label}
                fullWidth
                value={value}
                onChange={(e) => handleChange(key, e.target.value)}
                {...commonProps}>
                {field.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            );

          case "radio":
            return (
              <FormControl key={index} error={showError} fullWidth>
                <FormLabel>{field.label}</FormLabel>
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) => handleChange(key, e.target.value)}>
                  {field.options.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      value={opt}
                      control={<Radio />}
                      label={opt}
                    />
                  ))}
                </RadioGroup>
                {showError && (
                  <Typography color="error" variant="caption">
                    {validationErrors[key]}
                  </Typography>
                )}
              </FormControl>
            );

          case "color":
            return (
              <FormControl key={index} error={showError} fullWidth>
                <FormLabel>{field.label}</FormLabel>
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  {field.options.map((color) => (
                    <Chip
                      key={color}
                      label={color}
                      onClick={() => handleChange(key, color)}
                      sx={{
                        backgroundColor: color,
                        color: "#fff",
                        border:
                          value === color
                            ? "2px solid black"
                            : "1px solid #ccc",
                      }}
                    />
                  ))}
                </Box>
                {showError && (
                  <Typography color="error" variant="caption">
                    {validationErrors[key]}
                  </Typography>
                )}
              </FormControl>
            );

          case "number":
            return (
              <TextField
                key={index}
                label={field.label}
                type="number"
                fullWidth
                value={value}
                onChange={(e) => handleChange(key, Number(e.target.value))}
                {...commonProps}
              />
            );

          case "checkbox":
            return (
              <FormGroup key={index}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!value}
                      onChange={(e) => handleChange(key, e.target.checked)}
                    />
                  }
                  label={field.label}
                />
              </FormGroup>
            );

          case "multiselect":
            return (
              <FormControl key={index} component="fieldset" fullWidth>
                <FormLabel component="legend">{field.label}</FormLabel>
                <FormGroup row>
                  {field.options.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          checked={(value || []).includes(opt)}
                          onChange={() => handleCheckboxChange(key, opt)}
                        />
                      }
                      label={opt}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            );

          case "text":
            return (
              <TextField
                key={index}
                label={field.label}
                fullWidth
                value={value}
                onChange={(e) => handleChange(key, e.target.value)}
                {...commonProps}
              />
            );

          default:
            return null;
        }
      })}

      {/* Disabled warning */}
      {isCombinationDisabled(customizationValues, disabledCombinations) && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mt: 2, fontWeight: 500, width: "100%" }}>
          This combination is currently unavailable.
        </Typography>
      )}
    </Box>
  );
};

export default AccessoryCustomizationForm;
